import { CheckIcon, PencilIcon, XIcon } from '@heroicons/react/outline';
import { useEffect, useRef, useState } from 'react';

import InfoTooltip from '../../components/InfoTooltip';

/**
 * Field label with its optional required marker and info tooltip. Shared between
 * display and edit mode; edit mode passes `required` so the marker only shows
 * there (display mode has no marker), while the label text and tooltip render
 * the same in both. The uppercase/size styling lives on the label text only —
 * keeping it off the wrapper so it can't bleed into the tooltip content.
 */
const FieldLabel = ({
  label,
  required,
  tooltip,
}: {
  label: string;
  required?: boolean;
  tooltip?: string;
}) => (
  <div className="yst-flex yst-items-center yst-gap-1">
    <span className="yst-text-[10px] yst-font-semibold yst-uppercase yst-tracking-wider yst-text-slate-500">
      {label}
      {required && (
        <span className="yst-ml-0.5 yst-text-red-600" aria-hidden="true">
          *
        </span>
      )}
    </span>
    {tooltip && <InfoTooltip label={label} content={tooltip} />}
  </div>
);

type CardProps = {
  label: string;
  /** Display value (already formatted). */
  displayValue: string;
  /** Edit-mode initial value (raw string for input). */
  editInitialValue: string;
  /** Render a custom edit input (e.g., textarea or multi-field). Receives current draft + setter. */
  renderEditField?: (
    draft: string,
    setDraft: (value: string) => void,
    onSubmitKey: (event: React.KeyboardEvent) => void,
  ) => React.ReactNode;
  /** Disable hover/edit affordances while the assistant is streaming. */
  disabled: boolean;
  /** Called with the edited string when the user confirms. */
  onSubmit: (value: string) => void;
  /** Position in the batch of cards mounting together; drives the entry animation delay. */
  staggerIndex?: number;
  /** Hard character cap for the default text input (e.g. the brand description). */
  maxLength?: number;
  /** Marks the field required: shows a marker in edit mode and blocks saving it empty. */
  required?: boolean;
  /** Input type for the default text input (e.g. 'url' for the website field). */
  inputType?: 'text' | 'url';
  /** Render the default edit field as a multi-line textarea (e.g. the long brand description). */
  multiline?: boolean;
  /** Optional validator run on the trimmed draft; returns an error message when invalid, else null. */
  validate?: (value: string) => string | null;
  /** Short explanatory copy shown in an info tooltip next to the label. */
  tooltip?: string;
};

const BrandProfileFieldCard = ({
  label,
  displayValue,
  editInitialValue,
  renderEditField,
  disabled,
  onSubmit,
  staggerIndex = 0,
  maxLength,
  required = false,
  inputType = 'text',
  multiline = false,
  validate,
  tooltip,
}: CardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(editInitialValue);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isEditing) {
      setDraft(editInitialValue);
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [isEditing, editInitialValue]);

  // Measure against the trimmed value — that's what actually gets saved (the
  // onSubmit handlers trim/normalize), so leading/trailing whitespace can't
  // block saving or inflate the counter.
  const trimmedDraft = draft.trim();
  const isOverLimit = maxLength != null && trimmedDraft.length > maxLength;
  // A required field can't be saved empty (mirrors the over-limit block below).
  const isRequiredEmpty = required && trimmedDraft.length === 0;
  // Field-specific validation (e.g. URL check for the website field). Runs on
  // the trimmed value — what actually gets saved.
  const validationError = validate ? validate(trimmedDraft) : null;

  const cancel = () => {
    setIsEditing(false);
    setDraft(editInitialValue);
  };

  const confirm = () => {
    // Block saving over-length text rather than truncating it: the user keeps
    // exactly what they typed/pasted and shortens it themselves. Submit the
    // trimmed value so what we validated is what we save.
    if (isOverLimit || isRequiredEmpty || validationError) return;
    onSubmit(trimmedDraft);
    setIsEditing(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      confirm();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
    }
  };

  if (isEditing) {
    return (
      <div className="yst-rounded-lg yst-border-2 yst-border-primary-500 yst-bg-white yst-p-3 yst-shadow-sm">
        <FieldLabel label={label} required={required} tooltip={tooltip} />
        <div className={`yst-mt-1 yst-flex yst-gap-2 ${multiline ? 'yst-items-start' : 'yst-items-center'}`}>
          <div className="yst-flex-1 yst-min-w-0">
            {renderEditField ? (
              renderEditField(draft, setDraft, handleKeyDown)
            ) : multiline ? (
              <textarea
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                rows={4}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                aria-label={label}
                aria-required={required}
                aria-invalid={isRequiredEmpty || isOverLimit || validationError != null}
                className="yst-w-full yst-resize-y yst-rounded yst-border-0 yst-bg-slate-100 yst-px-2 yst-py-1 yst-text-sm yst-text-slate-900 focus:yst-outline-none focus:yst-ring-1 focus:yst-ring-primary-500"
              />
            ) : (
              <input
                ref={inputRef as React.RefObject<HTMLInputElement>}
                type={inputType}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                aria-label={label}
                aria-required={required}
                aria-invalid={isRequiredEmpty || isOverLimit || validationError != null}
                className="yst-w-full yst-rounded yst-border-0 yst-bg-slate-100 yst-px-2 yst-py-1 yst-text-sm yst-text-slate-900 focus:yst-outline-none focus:yst-ring-1 focus:yst-ring-primary-500"
              />
            )}
          </div>
          <button
            type="button"
            onClick={confirm}
            disabled={disabled || isOverLimit || isRequiredEmpty || validationError != null}
            aria-label={`Save ${label}`}
            className="yst-flex yst-h-6 yst-w-6 yst-items-center yst-justify-center yst-rounded yst-text-emerald-600 hover:yst-bg-emerald-50 disabled:yst-cursor-not-allowed disabled:yst-opacity-50"
          >
            <CheckIcon className="yst-h-5 yst-w-5" />
          </button>
          <button
            type="button"
            onClick={cancel}
            aria-label={`Cancel editing ${label}`}
            className="yst-flex yst-h-6 yst-w-6 yst-items-center yst-justify-center yst-rounded yst-text-slate-500 hover:yst-bg-slate-100"
          >
            <XIcon className="yst-h-5 yst-w-5" />
          </button>
        </div>
        {isRequiredEmpty && (
          <div className="yst-mt-1 yst-text-[10px] yst-text-red-600" aria-live="polite">
            This field is required.
          </div>
        )}
        {validationError && !isRequiredEmpty && (
          <div className="yst-mt-1 yst-text-[10px] yst-text-red-600" aria-live="polite">
            {validationError}
          </div>
        )}
        {maxLength != null && !renderEditField && (
          <div
            className={`yst-mt-1 yst-text-right yst-text-[10px] ${
              isOverLimit ? 'yst-text-red-600' : 'yst-text-slate-400'
            }`}
          >
            <span aria-live="polite" className="yst-mr-1">
              {isOverLimit ? 'Too long to save — please shorten it.' : ''}
            </span>
            {trimmedDraft.length}/{maxLength}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="yst-group yst-relative yst-rounded-lg yst-border yst-border-slate-200 yst-bg-white yst-p-3 yst-transition-colors hover:yst-border-slate-300 yst-animate-fade-up"
      style={{ '--yst-stagger-delay': `${staggerIndex * 120}ms` } as React.CSSProperties}
    >
      <div className="yst-flex yst-items-start yst-justify-between yst-gap-2">
        <FieldLabel label={label} tooltip={tooltip} />
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          disabled={disabled}
          aria-label={`Edit ${label}`}
          className="yst-flex yst-h-5 yst-w-5 yst-items-center yst-justify-center yst-rounded yst-text-slate-500 yst-opacity-0 yst-transition-opacity hover:yst-bg-slate-100 group-hover:yst-opacity-100 focus:yst-opacity-100 disabled:yst-cursor-not-allowed"
        >
          <PencilIcon className="yst-h-4 yst-w-4" />
        </button>
      </div>
      <div className="yst-mt-1 yst-text-sm yst-whitespace-pre-wrap yst-break-words">
        {displayValue.trim().length > 0 ? (
          <span className="yst-text-slate-900">{displayValue}</span>
        ) : (
          <span className="yst-italic yst-text-slate-400">Not added yet</span>
        )}
      </div>
    </div>
  );
};

export default BrandProfileFieldCard;
